"use client";

/**
 * Ganchos de gesto, sentido e destaque.
 *
 * - `useTremor`: o "não" discreto (4px, três idas). Só quando a pessoa TENTOU
 *   (clicou em Salvar com campo errado, senha recusada). Nunca enquanto digita.
 * - `useSentido`: +1 quando o índice cresce, -1 quando volta. É o que faz a
 *   aba da direita entrar pela direita e o "voltar" entrar pela esquerda.
 * - `useDestaque`: o fundo acende e apaga quando um dado muda sozinho (linha
 *   que chegou em tempo real, célula que atualizou). Aplique na CÉLULA, não na
 *   linha: fundo em linha de tabela passa por cima da zebra e da seleção.
 */
import { useAnimate, type AnimationOptions, type DOMKeyframesDefinition } from "motion/react";
import { useCallback, useEffect, useRef } from "react";
import { useMovimento } from "./provider";

export function useTremor<T extends Element = HTMLElement>() {
  const [escopo, animar] = useAnimate<T>();
  const mov = useMovimento();
  const tremer = useCallback(() => {
    const alvo = escopo.current;
    const tremor = mov.r.tremor;
    if (!alvo || !tremor || mov.escala === 0) return;
    animar(alvo, { x: tremor.x }, tremor.transition);
  }, [animar, escopo, mov]);
  return [escopo, tremer] as const;
}

export function useSentido(indice: number): 1 | -1 {
  const ref = useRef<{ indice: number; sentido: 1 | -1 }>({ indice, sentido: 1 });
  if (indice !== ref.current.indice) {
    ref.current = { indice, sentido: indice > ref.current.indice ? 1 : -1 };
  }
  return ref.current.sentido;
}

/**
 * Acende o fundo do elemento a cada vez que `pulso` muda (um contador que o
 * dono incrementa quando o dado muda). `naChegada` acende também na primeira
 * pintura (a linha que acabou de entrar).
 */
export function useDestaque<T extends Element = HTMLElement>(pulso: number, { naChegada = false } = {}) {
  const mov = useMovimento();
  const [escopo, animar] = useAnimate<T>();
  const visto = useRef(naChegada ? pulso - 1 : pulso);
  useEffect(() => {
    if (visto.current === pulso || !escopo.current) return;
    const antes = visto.current;
    visto.current = pulso;
    const { transition, ...alvo } = mov.r.destaque;
    const el = escopo.current as unknown as HTMLElement;
    /* No fim o fundo SAI do inline: se ficasse, vencia o fundo que o CSS pinta
       depois (linha selecionada, hover), e a célula ficava presa no véu 0. */
    void animar(escopo.current, alvo as DOMKeyframesDefinition, transition as AnimationOptions).then(() =>
      el.style?.removeProperty("background-color"),
    );
    /* No StrictMode o efeito monta, desmonta (o `useAnimate` para o que
       estava andando) e monta de novo: devolver o marcador deixa a segunda
       montagem acender de verdade. */
    return () => {
      visto.current = antes;
    };
  }, [pulso, animar, escopo, mov]);
  return escopo;
}
